// frontend/src/pages/ExplorePreview.js

import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDown,
  ArrowCircleDown,
  CalendarBlank,
  CheckCircle,
  Fire,
  Target,
} from "phosphor-react";

import logoRings from "../assets/third-place-logo4.png";

import PreLoginHeader from "../components/PreLoginHeader";
import ActivityCard from "../components/ActivityCard";
import ExploreCirclesPreview from "./ExploreCirclesPreview";
import ProfileCardPreview from "../components/ProfileCardPreview";

import {
  PREVIEW_USER,
  PREVIEW_SHOUTOUTS,
} from "../data/previewData";

/* -----------------------------------------------------------
   DEMO USERS
------------------------------------------------------------ */

const DEMO_USERS = {
  emily: {
    id: 9001,
    name: "Jade Kim",
    display_name: "Jade Kim",
    username: "thejadepillar",
    profile_pic_url: "/images/jadepropic1.jpg",
  },

  maya: {
    id: 9002,
    name: "Maya Rodriguez",
    display_name: "Maya Rodriguez",
    username: "mayamoves",
    profile_pic_url: "/images/mayapropic2.png",
  },

  daniel: {
    id: 9003,
    name: "Reeves Li",
    display_name: "Reeves Li",
    username: "danielli1225",
    profile_pic_url: "/images/reevespropic1.jpg",
  },

  sophie: {
    id: 9004,
    name: "Aaliyah Harris",
    display_name: "Aaliyah Harris",
    username: "aharris214",
    profile_pic_url: "images/aaliyahpropic1.jpg",
  },

  alex: {
    id: 9005,
    name: "Dhruv Chatterjee",
    display_name: "Dhruv Chatterjee",
    username: "dhruvchat",
    profile_pic_url: "images/dhruvpropic1.jpg",
  },

  noah: {
    id: 9006,
    name: "Yuna Moon",
    display_name: "Yuna Moon",
    username: "yunamoon",
    profile_pic_url: "images/yunapropic1.jpg",
  },
};

/* -----------------------------------------------------------
   DEMO CIRCLES
------------------------------------------------------------ */

const DEMO_CIRCLES = [
    {
    id: 8104,
    name: "Home Cooks",
    description: "Weeknight experiments and recipes worth repeating.",
    members: 96,
    image_url:
      "/images/homecookscatpropic1.png",
  },
  {
    id: 8101,
    name: "NYC Beginner Guitar",
    description: "Morning miles, weekend races, and post-run coffee.",
    members: 248,
    image_url:
      "images/guitarcircle1.png"
  },
  {
    id: 8102,
    name: "Joyful Art Cafe",
    description: "A little creative progress every day.",
    members: 184,
    image_url:
      "https://images.unsplash.com/photo-1541961017774-22349e4a1262?auto=format&fit=crop&w=300&q=80",
  },
  {
    id: 8103,
    name: "Solidform",
    description: "Current reads, cozy cafés, and monthly meetups.",
    members: 132,
    image_url:
      "images/solidcorecircle1.png"
  },
];

/* -----------------------------------------------------------
   DYNAMIC DEMO DATES

   These are generated relative to today so ActivityCard continues
   displaying "Today" and "Yesterday" rather than old fixed dates.
------------------------------------------------------------ */

const hoursAgo = (hours) => {
  const date = new Date();
  date.setHours(date.getHours() - hours);
  return date.toISOString();
};

/* -----------------------------------------------------------
   DEMO ACTIVITIES

   These use the same data shape ActivityCard already expects.
------------------------------------------------------------ */

const DEMO_ACTIVITIES = [
  {
    id: 7001,
    user_id: DEMO_USERS.maya.id,
    user: DEMO_USERS.maya,
    title: "Solidform 100th class!",
    description:
      "Still can't believe I made it to 100 classes. Finally nailed a full set of slow mountain climbers without needing to come out of the move!",
    mood: "LFG",
    duration: "50 minutes",
    location: "Solidform SoHo",
    timestamp: hoursAgo(1),
    tags: ["solidform", "pilates", "reformerpilates"],
    circles: [
      { id: 8101, name: "Solidform" },
      { id: 8107, name: "NYC Pilates" },
    ],
    media_urls: [
      "/images/solidcore1.png",
      "/images/solidcore2.png",
    ],
    demo_comments: [
      {
        id: 9201,
        content: "LFG !!!",
        created_at: hoursAgo(0.7),
        star_count: 3,
        starred_by_me: false,
        user: DEMO_USERS.emily,
      },
      {
        id: 9202,
        content: "The slow mountain climbers are genuinely brutal 😭",
        created_at: hoursAgo(0.4),
        star_count: 1,
        starred_by_me: true,
        user: DEMO_USERS.sophie,
      },
    ],
    high_five_count: 18,
    has_high_fived: true,
    comment_count: 4,
    highfivers_preview: [
      DEMO_USERS.emily,
      DEMO_USERS.daniel,
      DEMO_USERS.sophie,
    ],
    commenters_preview: [DEMO_USERS.alex],
  },

  {
    id: 7002,
    user_id: DEMO_USERS.daniel.id,
    user: DEMO_USERS.daniel,
    title: "Made kimchi fried rice from scratch",
    description:
      "Best lazy dinner recipe: kimchi, microwavable rice, gochujang, soy sauce, and two eggs. Used extra kimchi juice and added the sesame oil after turning off the heat. Definitely making this again.",
    mood: "Satisfied",
    duration: "20 minutes",
    location: "Tribeca, New York",
    timestamp: hoursAgo(3),
    tags: ["cooking", "koreanfood", "homemade"],
    circles: [{ id: 8104, name: "Home Cooks" }],
    media_url: "/images/kimchifriedrice2.png",
    high_five_count: 26,
    comment_count: 7,
    highfivers_preview: [
      DEMO_USERS.maya,
      DEMO_USERS.sophie,
      DEMO_USERS.noah,
    ],
    commenters_preview: [DEMO_USERS.emily, DEMO_USERS.alex],
    demo_comments: [
      {
        id: 9303,
        content:
          "Yum!",
        created_at: hoursAgo(3.4),
        star_count: 1,
        starred_by_me: true,
        user: DEMO_USERS.alex,
      },
      {
        id: 9304,
        content:
          "Making this tomorrow!",
        created_at: hoursAgo(0.8),
        star_count: 2,
        starred_by_me: true,
        user: DEMO_USERS.noah,
      },
    ],
  },

  {
    id: 7003,
    user_id: DEMO_USERS.sophie.id,
    user: DEMO_USERS.sophie,
    title: "Finished my first pottery bowl",
    description:
      "It is not perfectly symmetrical, but I think that is exactly why I love it :') Will be my new morning matcha bowl.",
    mood: "",
    duration: "2 hours",
    location: "Bklyn Clay",
    timestamp: hoursAgo(6),
    tags: ["pottery", "creative", "first"],
    circles: [
      { id: 8111, name: "Bklyn Clay" },
      { id: 8114, name: "Beginner Pottery" },
    ],
    media_urls: ["/images/matchabowl1.png"],
    high_five_count: 34,
    comment_count: 9,
    highfivers_preview: [
      DEMO_USERS.emily,
      DEMO_USERS.maya,
      DEMO_USERS.daniel,
      DEMO_USERS.alex,
    ],
    commenters_preview: [DEMO_USERS.noah],
  },

  {
    id: 7004,
    user_id: DEMO_USERS.alex.id,
    user: DEMO_USERS.alex,
    title: "Shipped the first version",
    description:
      "A small milestone, but a meaningful one. The idea is officially out of my notes app and in the world.",
    mood: "Excited",
    duration: "3 focused hours",
    location: "Brooklyn, New York",
    timestamp: hoursAgo(10),
    tags: ["building", "milestone", "product"],
    circles: [
      { id: 8105, name: "Indie Builders" },
      { id: 8107, name: "Python 101" },
    ],
    high_five_count: 42,
    comment_count: 12,
    highfivers_preview: [
      DEMO_USERS.emily,
      DEMO_USERS.maya,
      DEMO_USERS.daniel,
      DEMO_USERS.sophie,
    ],
    commenters_preview: [DEMO_USERS.noah],
  },

  {
    id: 7005,
    user_id: DEMO_USERS.noah.id,
    user: DEMO_USERS.noah,
    title: "Guitar lesson",
    description:
      "Went to my first in-person lesson through the NYC Beginner Guitar circle tonight! We learned the G, C, and D chords, and I played my first chord progression without stopping. Excited for next week's class!",
    mood: "Taylor Swift",
    duration: "45 minutes",
    location: "New York City Guitar School",
    timestamp: hoursAgo(23),
    tags: ["guitar", "music", "beginnerguitar"],
    circles: [
      { id: 8101, name: "NYC Beginner Guitar" },
      { id: 8107, name: "New York City Guitar School" },
    ],
    media_urls: ["/images/guitar1.mov"],
    high_five_count: 15,
    comment_count: 6,
    highfivers_preview: [
      DEMO_USERS.emily,
      DEMO_USERS.sophie,
      DEMO_USERS.alex,
    ],
    commenters_preview: [DEMO_USERS.maya],
  },

  {
    id: 7006,
    user_id: DEMO_USERS.emily.id,
    user: DEMO_USERS.emily,
    title: "At-home wine tasting #3",
    description:
      "This month's sip & yap featuring a line up of white burgundys.",
    mood: "Restored",
    duration: "1 hour",
    location: "West Village, NYC",
    timestamp: hoursAgo(28),
    tags: ["wine", "friends", "winetasting"],
    circles: [
      { id: 8101, name: "Sip & Yap" },
      { id: 8107, name: "No Wine Snobs" },
    ],
    media_urls: [
      "images/wine2.jpg",
      "images/charcut1.jpg",
    ],
    high_five_count: 21,
    comment_count: 3,
    highfivers_preview: [
      DEMO_USERS.maya,
      DEMO_USERS.daniel,
      DEMO_USERS.noah,
    ],
    commenters_preview: [DEMO_USERS.sophie],
  },
];

/* -----------------------------------------------------------
   SMALL PRESENTATIONAL COMPONENTS
------------------------------------------------------------ */

const DemoPlannerPanel = () => {
  const items = [
    {
      icon: CalendarBlank,
      label: "Monthly Calendar",
      value: "12 days",
    },
    {
      icon: Fire,
      label: "Current Streak",
      value: "6 days",
    },
    {
      icon: Target,
      label: "July Goals",
      value: "3 of 5",
    },
    {
      icon: CheckCircle,
      label: "Weekly Plan",
      value: "On track",
    },
  ];

  return (
    <section className="mt-4 rounded-2xl bg-white p-5">
      <p className="mb-1 px-1 sm:text-[16px] tracking-[-0.02em] font-black text-blue-600">
        Your Progress
      </p>

      <div className="space-y-1">
        {items.map(({ icon: Icon, label, value }) => (
          <div
            key={label}
            className="flex items-center font-semibold justify-between rounded-xl px-2 py-2.5"
          >
            <div className="flex min-w-0 items-center gap-2">
              <Icon
                size={17}
                weight="duotone"
                className="shrink-0 text-blue-600"
              />

              <span className="truncate text-sm text-gray-700">{label}</span>
            </div>

            <span className="ml-2 shrink-0 text-sm font-medium text-gray-500">
              {value}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

const DemoCirclesPanel = ({
  onExploreCircles,
  onHomeCooks,
}) => {
  return (
    <section className="rounded-2xl bg-white p-5">
      <div className="mb-3 flex items-center">
        <button
          type="button"
          onClick={onExploreCircles}
          className="
            group flex !min-h-0 !min-w-0 items-center gap-2
            bg-transparent p-0 text-left
          "
          aria-label="Preview popular circles"
        >
          <img
            src={logoRings}
            alt=""
            className="h-4 w-4 object-contain"
            style={{
              filter:
                "brightness(0) saturate(100%) invert(28%) sepia(96%) saturate(2785%) hue-rotate(204deg) brightness(97%) contrast(94%)",
            }}
          />

          <h2 className="text-[16px] font-black tracking-[-0.02em] text-blue-600">
            Popular Circles
          </h2>
        </button>
      </div>

      <div className="space-y-2">
        {DEMO_CIRCLES.map((circle) => {
          const isHomeCooks = circle.id === 8104;

          return (
            <div key={circle.id} className="flex items-center gap-3">
              {isHomeCooks ? (
                <button
                  type="button"
                  onClick={onHomeCooks}
                  className="
                    group relative shrink-0
                    !min-h-0 !min-w-0
                    rounded-full bg-transparent p-0
                  "
                  aria-label="Preview the Home Cooks circle"
                >
                  <img
                    src={circle.image_url}
                    alt={circle.name}
                    className="
                      relative h-11 w-11 rounded-full object-cover
                      ring-4 ring-blue-500
                      animate-[wiggle_1.8s_ease-in-out_infinite]
                      transition
                      group-hover:scale-105
                    "
                  />

                  <span
                    className="
                      absolute -bottom-1 -right-1
                      grid h-5 w-5 place-items-center
                      rounded-full bg-blue-600 text-white
                      shadow-sm
                    "
                  >
                    <ArrowDown size={11} weight="bold" />
                  </span>
                </button>
              ) : (
                <img
                  src={circle.image_url}
                  alt={circle.name}
                  className="h-11 w-11 shrink-0 rounded-full object-cover"
                />
              )}

              <div className="min-w-0 flex-1">
                {isHomeCooks ? (
                  <button
                    type="button"
                    onClick={onHomeCooks}
                    className="
                      block w-full
                      !min-h-0 !min-w-0
                      bg-transparent p-0 text-left
                    "
                    aria-label="Preview the Home Cooks circle"
                  >
                    <p className="truncate text-sm font-bold text-gray-800 transition hover:text-blue-600">
                      {circle.name}
                    </p>

                    <p className="truncate text-xs text-gray-500">
                      {circle.members} members
                    </p>
                  </button>
                ) : (
                  <>
                    <p className="truncate text-sm font-bold text-gray-800">
                      {circle.name}
                    </p>

                    <p className="truncate text-xs text-gray-500">
                      {circle.members} members
                    </p>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onExploreCircles}
        className="
          mt-3
          inline-flex
          flex-col
          items-center
          gap-1
          bg-transparent
          p-0
          text-blue-600
          transition
          hover:text-blue-700
        "
      >
        <span className="inline-flex items-center gap-1 text-sm font-semibold">
          <ArrowCircleDown
            size={22}
            weight="fill"
            className="text-blue-600"
          />
          Meet your next <span className="text-blue-500">third place</span>
        </span>

        <svg
          className="wave-underline -mt-1 ml-6 h-2 w-[170px]"
          viewBox="0 0 190 8"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="
              M0 4
              Q8 0 16 4
              T32 4
              T48 4
              T64 4
              T80 4
              T96 4
              T112 4
              T128 4
              T144 4
              T160 4
              T176 4
              T192 4
            "
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </section>
  );
};

const DemoConnectionsCard = () => {
  const people = [
    DEMO_USERS.maya,
    DEMO_USERS.daniel,
    DEMO_USERS.sophie,
    DEMO_USERS.alex,
  ];

  return (
    <section className="mt-4 rounded-2xl bg-white p-5">
      <h2 className="mb-3 sm:text-[16px] tracking-[-0.02em] font-black text-blue-600">
        Your Connections
      </h2>

      <div className="flex -space-x-2">
        {people.map((person) => (
          <img
            key={person.id}
            src={person.profile_pic_url}
            alt={person.display_name}
            title={person.display_name}
            className="h-10 w-10 rounded-full object-cover ring-2 ring-white"
          />
        ))}

        <div className="grid h-10 w-10 place-items-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700 ring-2 ring-white">
          +18
        </div>
      </div>

      <p className="mt-3 text-sm leading-tight font-medium text-gray-600">
        Go further, together!
      </p>
    </section>
  );
};

/* -----------------------------------------------------------
   EXPLORE PREVIEW PAGE
------------------------------------------------------------ */

function ExplorePreview({ embedded = false }) {
  const navigate = useNavigate();

  const leftRailRef = React.useRef(null);
  const rightRailRef = React.useRef(null);

  const [railHeight, setRailHeight] =
    React.useState(null);

  React.useEffect(() => {
    const updateRailHeight = () => {
      if (window.innerWidth < 768) {
        setRailHeight(null);
        return;
      }

      const leftHeight =
        leftRailRef.current?.scrollHeight || 0;

      const rightHeight =
        rightRailRef.current?.scrollHeight || 0;

      setRailHeight(
        Math.max(leftHeight, rightHeight)
      );
    };

    updateRailHeight();

    const observer = new ResizeObserver(
      updateRailHeight
    );

    if (leftRailRef.current) {
      observer.observe(leftRailRef.current);
    }

    if (rightRailRef.current) {
      observer.observe(rightRailRef.current);
    }

    window.addEventListener(
      "resize",
      updateRailHeight
    );

    return () => {
      observer.disconnect();

      window.removeEventListener(
        "resize",
        updateRailHeight
      );
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);

    if (!element) return;

    const y =
      element.getBoundingClientRect().top +
      window.pageYOffset +
      2;

    window.scrollTo({
      top: y,
      behavior: "smooth",
    });
  };

  const handlePreviewAction = () => {
    navigate("/signup");
  };

  return (
    <div className="min-h-screen bg-blue-50 text-[#181411]">
      {!embedded && <PreLoginHeader />}

      {/* Hero */} 
      <section className="px-6 pb-6 pt-[4.45rem] text-center md:px-10 sm:pt-24 lg:pb-8">
          <h1 className="mx-auto max-w-3xl text-2xl font-black leading-[0.95] tracking-[-0.03em] text-gray-900 lg:text-[38px]">
            Rediscover your {" "}
            <span className="whitespace-nowrap text-blue-500">
              third place
            </span>
          </h1>

        <p className="mx-auto mt-4 max-w-4xl text-sm leading-tight sm:tracking-[-0.02em] text-gray-600 sm:text-[20px]">
          {/* Desktop */}
          <span className="hidden sm:inline">
            In sociology, a "third place" is where we connect beyond home and work
            <br />
            — we're bringing that idea into the digital age
          </span>

          {/* Mobile */}
          <span className="sm:hidden">
            In sociology, a "third place" is where
            <br />
            we connect beyond home and work
            <br />
            — we're bringing that idea into the digital age
          </span>
        </p>
      </section>

      {/* Home-style application preview */}
      <main className="mx-auto w-full max-w-7xl px-0 pb-4 sm:pb-8 md:px-10 xl:px-16">
        {/* MOBILE PROFILE */}
        <div className="px-5 pb-4 md:hidden">
          <ProfileCardPreview
            user={PREVIEW_USER}
            shoutouts={PREVIEW_SHOUTOUTS}
            onPreviewAction={handlePreviewAction}
          />
        </div>

        <div
          className="
            grid grid-cols-1 gap-8
            md:grid-cols-[1fr_minmax(0,2.15fr)_1fr]
            md:overflow-visible
          "
        >
          {/* LEFT — same position/proportion as Home */}
          <aside
            ref={leftRailRef}
            className="
              hidden
              md:sticky md:top-4 md:block md:h-max md:self-start
            "
          >
            <ProfileCardPreview
              user={PREVIEW_USER}
              shoutouts={PREVIEW_SHOUTOUTS}
              onPreviewAction={handlePreviewAction}
            />

            <DemoPlannerPanel />
          </aside>

          {/* MIDDLE — real ActivityCard feed */}
          <section
            className="
              min-w-0
              sm:pr-0 sm:pl-0
              pl-4 pr-4
              self-stretch
              rounded-scroll

              md:flex
              md:flex-col
              md:overflow-hidden
            "
            style={{
              height:
                railHeight != null
                  ? `${railHeight}px`
                  : undefined,
            }}
          >
            <div className="relative z-10 mb-4 ml-2.5 flex items-center">
              <button
                type="button"
                onClick={handlePreviewAction}
                className="
                  !min-h-0 !min-w-0
                  rounded-full bg-blue-200 px-4 py-1 sm:py-2
                  text-sm font-bold text-blue-600
                  transition 
                  hover:ring-2 hover:ring-blue-200
                "
              >
                Following
              </button>

              <span className="ml-3 text-xs font-medium text-gray-500">
                A peek inside your home feed
              </span>
            </div>

            <div
              className="
                relative z-0
                min-h-0 flex-1
                space-y-4
                pr-0
                md:overflow-y-auto
                md:pb-28
                no-scrollbar
              "
            >
              {DEMO_ACTIVITIES.map((activity) => (
                <ActivityCard
                  key={activity.id}
                  activity={activity}
                  currentUser={DEMO_USERS.emily}
                  readOnly
                  contentClassName="px-6 py-5"
                  onTagClick={handlePreviewAction}
                />
              ))}
            </div>
          </section>

          {/* RIGHT — same position/proportion as Home */}
          <aside className="hidden self-stretch md:block">
            <div
              ref={rightRailRef}
              className="sticky top-4 h-max"
            >
              <button
                type="button"
                onClick={handlePreviewAction}
                className="
                  mb-4 w-full rounded-full bg-blue-600
                   !min-h-0 !min-w-0 !px-4 !py-2.5 sm:text-[16px] font-semibold text-white
                  transition hover:bg-blue-700
                "
              >
                Share an activity
              </button>

              <DemoCirclesPanel
                onExploreCircles={() =>
                  scrollToSection("explore-circles-preview")
                }
                onHomeCooks={() =>
                  scrollToSection("home-cooks-details-preview")
                }
              />
              <DemoConnectionsCard />
            </div>
          </aside>
        </div>
      </main>
      
      <section
        id="explore-circles-preview"
        className={`mt-3 bg-blue-50 pt-4 sm:pt-8 ${
          embedded ? "" : ""
        }`}
      >
        <div className="px-6 pb-4 sm:pb-8 text-center md:px-10">
          <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-blue-700">
            A place to belong
          </span>

          <h1 className="mx-auto mt-3 max-w-3xl text-2xl font-black leading-[0.95] tracking-[-0.03em] text-gray-900 sm:text-3xl lg:text-[38px]">
            Meet your next{" "}
            <span className="whitespace-nowrap text-blue-500">
              third place
            </span>
          </h1>

          <p className="mx-auto mt-4 max-w-4xl text-sm leading-tight tracking-[-0.02em] text-gray-600 sm:text-[20px]">
            Discover communities built around the things you love,
            <br className="hidden sm:block" />
            {" "} the goals you're chasing,
            <br className="sm:hidden" />
            {" "}and the people you'll grow with
          </p>
        </div>

        <ExploreCirclesPreview embedded />
      </section>

    </div>
  );
}

export default ExplorePreview;